import { ENDPOINTS } from "../Api/Endpoints";

/**
 * Presigned S3 upload flow: POST /app/prescriptions/upload-urls → PUT each file to uploadUrl
 * (Content-Type should match the file, as used when signing is optional per AWS SDK) →
 * POST /app/prescriptions with { items: [{ type, key, originalName }] }.
 *
 * If PUT fails partway, or POST fails after all PUTs, we POST /app/prescriptions/delete-pending-keys
 * so orphaned objects under prescriptions/{userId}/pending/ are removed from S3.
 */

export async function deletePendingKeysRequest(apiRequest, keys) {
  const list = (keys || []).filter(
    (k) => typeof k === "string" && k.includes("/pending/")
  );
  if (!list.length) return;
  try {
    await apiRequest({
      method: "POST",
      url: ENDPOINTS.PRESCRIPTION_DELETE_PENDING_KEYS,
      data: { keys: list },
    });
  } catch (e) {
    console.warn("deletePendingKeysRequest:", e?.message || e);
  }
}

/**
 * @param {object} params
 * @param {function} params.apiRequest - Api helper
 * @param {function} params.requestUploadUrls - () => Promise with upload-urls response
 * @param {function} params.getUploadsFromResponse
 * @param {function} params.uploadFileToS3 - (uploadUrl, file) => Promise
 * @param {function} params.submitPrescriptionItems - (itemsPayload) => Promise
 * @param {Array<{ type: string, file: File }>} params.selectedItems
 */
export async function runPresignedUploadWithRollback({
  apiRequest,
  requestUploadUrls,
  getUploadsFromResponse,
  uploadFileToS3,
  submitPrescriptionItems,
  selectedItems,
}) {
  const uploadUrlRes = await requestUploadUrls(selectedItems);
  const uploads = getUploadsFromResponse(uploadUrlRes);
  if (!Array.isArray(uploads) || uploads.length !== selectedItems.length) {
    throw new Error("Upload URL response mismatch");
  }

  const itemsPayload = uploads.map((upload, index) => {
    const file = selectedItems[index]?.file;
    const size = file?.size;
    return {
      type: upload?.type || selectedItems[index]?.type,
      key: upload?.key,
      originalName: file?.name,
      ...(Number.isFinite(size) && size > 0 ? { fileSize: size } : {}),
    };
  });

  const keysForRollback = uploads.map((u) => u?.key).filter(Boolean);
  const uploadedKeys = [];

  try {
    for (let i = 0; i < selectedItems.length; i += 1) {
      await uploadFileToS3(uploads[i].uploadUrl, selectedItems[i].file);
      if (uploads[i]?.key) uploadedKeys.push(uploads[i].key);
    }
    return await submitPrescriptionItems(itemsPayload);
  } catch (err) {
    await deletePendingKeysRequest(apiRequest, uploadedKeys.length ? uploadedKeys : keysForRollback);
    throw err;
  }
}
